import { SearchableContextMenuSubmenu } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  useCopyPartyInsuranceToRelatedMatters,
  useCopyPartyVehicleToRelatedMatters,
  useLinkObjectToRelatedMatters,
} from "@/lib/data/react-query/matters/matter-related-object";
import type { InsuranceRelatedMatterPlacement } from "@/lib/data/requests/matters/matter-insurance";
import type { VehicleRelatedMatterPlacement } from "@/lib/data/requests/matters/matter-vehicle";
import type { RelatedMatterObjectType } from "@/lib/data/requests/matters/matter-related-object";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { Copy, Link2 } from "lucide-react";
import { useState } from "react";

const permissionKeys = {
  incident: ACTION_PERMISSION_KEYS.matter,
  "incident-report": ACTION_PERMISSION_KEYS.matterIncidentReport,
  vehicle: ACTION_PERMISSION_KEYS.matterVehicle,
  insurance: ACTION_PERMISSION_KEYS.matterInsurance,
} as const;

export const MatterRelatedMatterContextMenu = ({
  matter,
  objectId,
  objectType,
  insuranceParty,
  vehicleParty,
  onClientInsuranceCopyRequested,
  onClientVehicleCopyRequested,
}: {
  matter: Matter;
  objectId: string;
  objectType: RelatedMatterObjectType;
  insuranceParty?: {
    connectionId: string;
    role: "Client" | "Defendant" | "OtherParty";
  };
  vehicleParty?: {
    connectionId: string;
    role: "Client" | "Defendant" | "OtherParty";
  };
  onClientInsuranceCopyRequested?: (matterIds: string[]) => void;
  onClientVehicleCopyRequested?: (matterIds: string[]) => void;
}) => {
  const { hasActionPermission, hasGrant } = useUser();
  const [search, setSearch] = useState("");
  const linkObject = useLinkObjectToRelatedMatters();
  const copyPartyInsurance = useCopyPartyInsuranceToRelatedMatters();
  const copyPartyVehicle = useCopyPartyVehicleToRelatedMatters();
  const canLink =
    objectType === "vehicle"
      ? hasActionPermission(
          ACTION_PERMISSION_KEYS.matterVehicle,
          ACTION_PERMISSION_VALUES.create,
        ) &&
        hasGrant(ACTION_PERMISSION_KEYS.matterVehicle, "view") &&
        hasActionPermission(
          ACTION_PERMISSION_KEYS.matterVehicle,
          ACTION_PERMISSION_VALUES.update,
        )
      : hasActionPermission(
          permissionKeys[objectType],
          ACTION_PERMISSION_VALUES.create,
        );
  const relatedMatters = Array.from(
    new Map(
      matter.relatedMatterConnections
        .filter((connection) => connection.relatedMatterId !== matter.id)
        .map((connection) => [
          connection.relatedMatterId,
          {
            id: connection.relatedMatterId,
            title:
              connection.title ||
              connection.relatedMatterTitle ||
              "Untitled matter",
          },
        ]),
    ).values(),
  );
  const hasRequiredPartyContext =
    (objectType !== "insurance" || Boolean(insuranceParty)) &&
    (objectType !== "vehicle" || Boolean(vehicleParty));
  if (
    !canLink ||
    !objectId ||
    !hasRequiredPartyContext ||
    relatedMatters.length === 0
  ) {
    return null;
  }

  const options = [
    { id: "copy-to-all", title: "Copy to all" },
    ...relatedMatters.filter((relatedMatter) =>
      relatedMatter.title.toLowerCase().includes(search.trim().toLowerCase()),
    ),
  ];
  const copyInsurance = (placements: InsuranceRelatedMatterPlacement[]) => {
    if (!insuranceParty) return;
    copyPartyInsurance.mutate({
      insuranceId: objectId,
      sourceMatterContactConnectionId: insuranceParty.connectionId,
      sourceMatterId: matter.id,
      placements,
    });
  };
  const copyVehicle = (placements: VehicleRelatedMatterPlacement[]) => {
    if (!vehicleParty) return;
    copyPartyVehicle.mutate({
      vehicleId: objectId,
      sourceMatterContactConnectionId: vehicleParty.connectionId,
      sourceMatterId: matter.id,
      placements,
    });
  };
  const isPending =
    linkObject.isPending ||
    copyPartyInsurance.isPending ||
    copyPartyVehicle.isPending;

  return (
    <>
      <SearchableContextMenuSubmenu
        title={
          <>
            <Link2 />
            Add to related matter
          </>
        }
        items={options}
        getItemKey={(item) => item.id}
        renderItem={(item) => (
          <>
            {item.id === "copy-to-all" ? <Copy /> : null}
            <span className="truncate">{item.title}</span>
          </>
        )}
        search={search}
        onSearchChange={setSearch}
        placeholder="Search related matters..."
        onOpenChange={(open) => {
          if (!open) setSearch("");
        }}
        disabledItemKeys={isPending ? options.map((item) => item.id) : []}
        onSelectItem={(item) => {
          if (isPending) return;
          const matterIds =
            item.id === "copy-to-all"
              ? relatedMatters.map((relatedMatter) => relatedMatter.id)
              : [item.id];
          if (objectType === "insurance" && insuranceParty) {
            if (insuranceParty.role === "Client") {
              onClientInsuranceCopyRequested?.(matterIds);
            } else {
              copyInsurance(
                matterIds.map((matterId) => ({
                  matterId,
                  placement: "other_party",
                })),
              );
            }
            return;
          }
          if (objectType === "vehicle" && vehicleParty) {
            if (vehicleParty.role === "Client") {
              onClientVehicleCopyRequested?.(matterIds);
            } else {
              copyVehicle(
                matterIds.map((matterId) => ({
                  matterId,
                  placement: "other_party",
                })),
              );
            }
            return;
          }
          linkObject.mutate({
            objectType,
            objectId,
            sourceMatterId: matter.id,
            matterIds,
          });
        }}
      />
    </>
  );
};
